import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiClock, FiShield, FiInfo } = FiIcons;

const Introduction = ({ onStart }) => {
  const [userInfo, setUserInfo] = useState({
    age: '',
    gender: '',
    activity: '',
    time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  });

  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleStart = () => {
    if (userInfo.age && userInfo.gender && userInfo.activity) {
      onStart(userInfo);
    }
  };

  const isFormValid = userInfo.age && userInfo.gender && userInfo.activity;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-6"
          >
            <SafeIcon icon={FiPlay} className="text-3xl text-white" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Flow-Kurzskala
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Bewertung Ihres Flow-Erlebens
          </p>
          <p className="text-sm text-gray-500">
            Entwickelt von Rheinberg, Vollmeyer & Engeser
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center p-4 bg-blue-50 rounded-xl"
          >
            <SafeIcon icon={FiInfo} className="text-2xl text-blue-600 mr-3" />
            <div>
              <h3 className="font-semibold text-gray-900">Was wird gemessen?</h3>
              <p className="text-sm text-gray-600">Flow-Erleben in Alltag und Arbeit</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center p-4 bg-green-50 rounded-xl"
          >
            <SafeIcon icon={FiClock} className="text-2xl text-green-600 mr-3" />
            <div>
              <h3 className="font-semibold text-gray-900">Dauer</h3>
              <p className="text-sm text-gray-600">Ca. 5-10 Minuten</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center p-4 bg-purple-50 rounded-xl"
          >
            <SafeIcon icon={FiShield} className="text-2xl text-purple-600 mr-3" />
            <div>
              <h3 className="font-semibold text-gray-900">Datenschutz</h3>
              <p className="text-sm text-gray-600">Anonym und vertraulich</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-50 rounded-xl p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Bevor wir beginnen, einige kurze Angaben:
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alter (Jahre)
              </label>
              <input
                type="number"
                min="1"
                max="120"
                value={userInfo.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ihr Alter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geschlecht
              </label>
              <select
                value={userInfo.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bitte wählen</option>
                <option value="weiblich">Weiblich</option>
                <option value="männlich">Männlich</option>
                <option value="divers">Divers</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Welche Tätigkeit haben Sie gerade unterbrochen?
            </label>
            <input
              type="text"
              value={userInfo.activity}
              onChange={(e) => handleInputChange('activity', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="z.B. Arbeiten am Computer, Lesen, Sport..."
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <button
            onClick={handleStart}
            disabled={!isFormValid}
            className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
              isFormValid
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transform hover:scale-105 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Assessment starten
          </button>
          
          <p className="text-sm text-gray-500 mt-4">
            Bitte beziehen Sie sich bei den Antworten auf die eben unterbrochene Tätigkeit.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Introduction;